/**
 * The button of the editorial refresh.
 *
 * Deliberately separate from `components/Button.tsx`: that one is also imported
 * by the Agents screens, so changing its variants would move a surface that is
 * out of scope. This one is only ever used by the container screens.
 *
 * Green is "publish" and stays rare — one real green button per screen. Blue is
 * the secondary action. The brand gradient is never a button.
 */
export type DsButtonVariant = 'primary' | 'action' | 'outline' | 'subtle' | 'ghost';
export type DsButtonSize = 'md' | 'lg' | 'touch';

const VARIANTS: Record<DsButtonVariant, string> = {
  // inset highlight is what keeps the filled buttons from reading as flat
  primary: 'bg-state-ok text-white border border-transparent shadow-[inset_0_1px_0_rgba(255,255,255,.15)] hover:brightness-110',
  action: 'bg-action text-white border border-transparent shadow-[inset_0_1px_0_rgba(255,255,255,.15)] hover:brightness-110',
  outline: 'bg-navy-700 text-white border border-navy-400 hover:bg-navy-600',
  subtle: 'bg-navy-800 text-white border border-navy-400 hover:bg-navy-600',
  ghost: 'bg-transparent text-navy-200 border border-transparent hover:bg-navy-600 hover:text-white',
};

const SIZES: Record<DsButtonSize, string> = {
  md: 'h-[30px] px-[14px] text-[13px] rounded-control',
  lg: 'h-[38px] px-[18px] text-[14px] rounded-surface',
  // 44px is the touch target the design calls out for mobile CTAs and menu rows
  touch: 'h-11 px-[18px] text-[14px] rounded-surface',
};

interface DsButtonProps {
  variant?: DsButtonVariant,
  size?: DsButtonSize,
  className?: string,
  children: React.ReactNode,
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void,
  isDisabled?: boolean,
  title?: string,
  ariaLabel?: string,
}

const DsButton = ({
  variant = 'outline',
  size = 'lg',
  className = '',
  children,
  onClick,
  isDisabled = false,
  title,
  ariaLabel,
}: DsButtonProps) => (
  <button
    type="button"
    title={title}
    aria-label={ariaLabel}
    disabled={isDisabled}
    onClick={onClick}
    className={[
      'b4-focus font-inter font-semibold leading-none whitespace-nowrap',
      'inline-flex items-center justify-center gap-2 transition-colors duration-[120ms]',
      SIZES[size],
      isDisabled
        ? 'bg-navy-600 text-navy-300 border border-transparent cursor-not-allowed shadow-none'
        : `${VARIANTS[variant]} cursor-pointer`,
      className,
    ].join(' ')}
  >
    {children}
  </button>
)

export default DsButton
